import React, { useMemo, useState } from "react";
import Button from "../components/atoms/Button";
import Text from "../components/atoms/Text";
import { useNotification } from "../context/NotificationContext";
import { useUser } from "../context/UserContext";

function formatTodoDate(value) {
  if (!value) {
    return "Just now";
  }

  const date =
    typeof value?.toDate === "function" ? value.toDate() : new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "Just now";
  }

  return date.toLocaleString();
}

export default function Todo() {
  const {
    currentUser,
    isAuthenticated,
    isLoadingUser,
    addTodo,
    toggleTodoCompletion,
    removeTodo,
  } = useUser();
  const { notify } = useNotification();
  const [draft, setDraft] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const todos = currentUser?.todos || [];

  const completedCount = useMemo(
    () => todos.filter((todo) => todo.completed).length,
    [todos],
  );

  async function handleAddTodo(event) {
    event.preventDefault();

    const trimmedDraft = draft.trim();

    if (!trimmedDraft) {
      notify("Write a task before adding it.", "error");
      return;
    }

    if (!currentUser?.id) {
      notify("Please sign in to save tasks.", "error");
      return;
    }

    setIsSaving(true);

    try {
      const result = await addTodo(trimmedDraft);

      if (result?.success) {
        setDraft("");
      }
    } catch (error) {
      console.error("Failed to add todo.", error);
      notify("Could not save the task to Firebase.", "error");
    } finally {
      setIsSaving(false);
    }
  }

  async function handleToggleTodo(todo) {
    try {
      await toggleTodoCompletion(todo.id);
    } catch (error) {
      console.error("Failed to update todo.", error);
      notify("Could not update that task.", "error");
    }
  }

  async function handleDeleteTodo(todoId) {
    try {
      await removeTodo(todoId);
    } catch (error) {
      console.error("Failed to delete todo.", error);
      notify("Could not delete that task.", "error");
    }
  }

  if (isLoadingUser) {
    return (
      <div style={{ padding: "64px 24px", backgroundColor: "var(--white)" }}>
        <Text size="px32" weight="w600" color="gray9">
          Loading your to-do list...
        </Text>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div style={{ padding: "64px 24px", backgroundColor: "var(--white)" }}>
        <div
          style={{
            maxWidth: "760px",
            margin: "0 auto",
            border: "1px solid var(--gray1)",
            borderRadius: "16px",
            padding: "32px",
            backgroundColor: "var(--white)",
          }}
        >
          <Text size="px32" weight="w600" color="gray9">
            Sign in to use your Firebase to-do list.
          </Text>
          <Text
            size="px16"
            weight="w400"
            color="gray6"
            extraStyles={{ marginTop: "12px", lineHeight: "160%" }}
          >
            Your tasks are stored inside your Firebase user profile, so they follow your account.
          </Text>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: "56px 24px 96px", backgroundColor: "#F7FAF7" }}>
      <div style={{ maxWidth: "860px", margin: "0 auto" }}>
        <div
          style={{
            background:
              "linear-gradient(135deg, rgba(0, 178, 7, 0.08), rgba(132, 209, 135, 0.18))",
            border: "1px solid rgba(32, 181, 38, 0.12)",
            borderRadius: "24px",
            padding: "32px",
            marginBottom: "24px",
          }}
        >
          <Text size="px32" weight="w600" color="gray9">
            My To-Do List
          </Text>
          <Text
            size="px16"
            weight="w400"
            color="gray6"
            extraStyles={{ marginTop: "10px", lineHeight: "160%" }}
          >
            {completedCount} of {todos.length} task{todos.length === 1 ? "" : "s"} completed.
          </Text>
        </div>

        <div
          style={{
            backgroundColor: "var(--white)",
            borderRadius: "20px",
            border: "1px solid var(--gray1)",
            boxShadow: "0 20px 48px rgba(0, 38, 3, 0.06)",
            overflow: "hidden",
          }}
        >
          <form
            onSubmit={handleAddTodo}
            style={{
              display: "flex",
              gap: "12px",
              padding: "24px",
              borderBottom: "1px solid var(--gray1)",
              flexWrap: "wrap",
            }}
          >
            <input
              type="text"
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              placeholder="Add a task you want to finish..."
              style={{
                flex: "1 1 320px",
                minHeight: "52px",
                borderRadius: "999px",
                border: "1px solid var(--gray2)",
                padding: "0 18px",
                fontSize: "15px",
                color: "var(--gray9)",
                outline: "none",
              }}
            />
            <Button
              type="submit"
              size="large"
              variant="fill"
              style={{ minWidth: "140px", justifyContent: "center" }}
            >
              {isSaving ? "Saving..." : "Add Task"}
            </Button>
          </form>

          {todos.length === 0 ? (
            <div style={{ padding: "32px 24px 40px" }}>
              <Text size="px18" weight="w400" color="gray5">
                No tasks yet. Add your first one.
              </Text>
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column" }}>
              {todos.map((todo) => (
                <div
                  key={todo.id}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "14px",
                    padding: "18px 24px",
                    borderTop: "1px solid var(--gray05)",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={Boolean(todo.completed)}
                    onChange={() => handleToggleTodo(todo)}
                    style={{ width: "18px", height: "18px", accentColor: "var(--primary)" }}
                  />

                  <div style={{ flex: 1, minWidth: 0 }}>
                    <Text
                      size="px18"
                      weight="w500"
                      color={todo.completed ? "gray5" : "gray9"}
                      extraStyles={{
                        textDecoration: todo.completed ? "line-through" : "none",
                        wordBreak: "break-word",
                      }}
                    >
                      {todo.title}
                    </Text>
                    <Text
                      size="px14"
                      weight="w400"
                      color="gray5"
                      extraStyles={{ marginTop: "4px" }}
                    >
                      Updated {formatTodoDate(todo.updatedAt || todo.createdAt)}
                    </Text>
                  </div>

                  <button
                    type="button"
                    onClick={() => handleDeleteTodo(todo.id)}
                    style={{
                      border: "1px solid var(--gray2)",
                      backgroundColor: "var(--white)",
                      color: "var(--gray6)",
                      borderRadius: "999px",
                      padding: "10px 16px",
                      cursor: "pointer",
                      fontSize: "14px",
                      fontWeight: 600,
                    }}
                  >
                    Delete
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
